import { teamService } from '@/services/teamService';
import {
  AddTeamMemberData,
  ProjectMember,
  RolePermissions,
  TeamPermission,
  TeamRole,
  TeamUser,
  UpdateTeamMemberData,
} from '@/types';
import { Check, UserPlus, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dropdown, Input, MultiDropdown, TextArea } from '../forms';

interface AddTeamMemberDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddTeamMemberData | UpdateTeamMemberData) => void;
  projectId: string;
  editingMember?: ProjectMember | null;
  mode?: 'add' | 'edit';
}

const AddTeamMemberDrawer: React.FC<AddTeamMemberDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  editingMember,
  mode = 'add',
}) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'viewer' as TeamRole,
    permissions: [] as string[],
    message: '',
    sendInvitation: true,
    status: 'active' as 'active' | 'inactive',
  });

  const [roles, setRoles] = useState<RolePermissions[]>([]);
  const [permissions, setPermissions] = useState<TeamPermission[]>([]);
  const [_availableUsers, _setAvailableUsers] = useState<TeamUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TeamUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUserSearch, setShowUserSearch] = useState(false);

  // Email checking state
  const [emailCheckState, setEmailCheckState] = useState<{
    isChecking: boolean;
    userExists: boolean;
    userData?: {
      firstName?: string;
      lastName?: string;
      name?: string;
    };
  }>({
    isChecking: false,
    userExists: false,
  });

  // Load roles and permissions
  useEffect(() => {
    const loadRolesAndPermissions = async () => {
      try {
        console.log('Loading roles and permissions...');
        const response = await teamService.getRolesAndPermissions();
        console.log('Roles and permissions response:', response);

        if (response.success && response.data) {
          console.log('Setting roles:', response.data.roles);
          console.log('Setting permissions:', response.data.permissions);
          setRoles(response.data.roles);
          setPermissions(response.data.permissions);
        } else {
          console.error('Failed to load roles and permissions:', response);
        }
      } catch (error) {
        console.error('Error loading roles and permissions:', error);
      }
    };

    if (isOpen) {
      loadRolesAndPermissions();
    }
  }, [isOpen]);

  // Initialize form data for editing
  useEffect(() => {
    if (mode === 'edit' && editingMember) {
      setFormData({
        email: editingMember.user?.email || '',
        firstName: '',
        lastName: '',
        role: editingMember.role,
        permissions: editingMember.permissions,
        message: '',
        sendInvitation: false,
        status: editingMember.status as 'active' | 'inactive',
      });
    } else {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        role: 'viewer',
        permissions: [],
        message: '',
        sendInvitation: true,
        status: 'active',
      });
    }

    // Reset email check state when opening/closing modal
    setEmailCheckState({
      isChecking: false,
      userExists: false,
    });
  }, [mode, editingMember, isOpen]);

  // Auto-update permissions when role changes
  useEffect(() => {
    const selectedRole = roles.find(r => r.role === formData.role);
    if (selectedRole) {
      setFormData(prev => ({
        ...prev,
        permissions: selectedRole.defaultPermissions,
      }));
    }
  }, [formData.role, roles]);

  // Real-time email checking
  useEffect(() => {
    const checkEmail = async () => {
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setEmailCheckState({
          isChecking: false,
          userExists: false,
        });
        return;
      }

      setEmailCheckState(prev => ({ ...prev, isChecking: true }));

      try {
        const response = await teamService.checkEmailAvailability(formData.email);
        if (response.success && response.data) {
          setEmailCheckState({
            isChecking: false,
            userExists: response.data.userExists,
            userData: response.data.userExists
              ? {
                  firstName: response.data.firstName,
                  lastName: response.data.lastName,
                  name: response.data.name,
                }
              : undefined,
          });

          // Clear first/last name if user exists (we'll show their existing info)
          if (response.data.userExists) {
            setFormData(prev => ({
              ...prev,
              firstName: '',
              lastName: '',
            }));
          }
        }
      } catch (error) {
        console.error('Error checking email:', error);
        setEmailCheckState({
          isChecking: false,
          userExists: false,
        });
      }
    };

    const timeoutId = setTimeout(checkEmail, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.email]);

  // Search users
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length >= 2) {
        const response = await teamService.searchUsers(searchQuery, projectId);
        if (response.success && response.data) {
          setSearchResults(response.data);
        }
      } else {
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, projectId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // For new users (email not exists), require first and last name
    if (mode === 'add' && !emailCheckState.userExists && !emailCheckState.isChecking) {
      if (!formData.firstName.trim()) {
        newErrors.firstName = 'First name is required for new users';
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = 'Last name is required for new users';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (mode === 'edit') {
        const updateData: UpdateTeamMemberData = {
          role: formData.role,
          permissions: formData.permissions,
          status: formData.status as any,
          message: formData.message || undefined,
        };
        await onSubmit(updateData);
      } else {
        const addData: AddTeamMemberData = {
          email: formData.email,
          role: formData.role,
          permissions: formData.permissions,
          message: formData.message || undefined,
          sendInvitation: formData.sendInvitation,
          // Include name data if it's a new user
          ...(!emailCheckState.userExists &&
            formData.firstName &&
            formData.lastName && {
              firstName: formData.firstName,
              lastName: formData.lastName,
            }),
        };
        await onSubmit(addData);
      }
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserSelect = (user: TeamUser) => {
    setFormData(prev => ({
      ...prev,
      email: user.email,
      firstName: '',
      lastName: '',
    }));
    setShowUserSearch(false);
    setSearchQuery('');

    // Update email check state
    setEmailCheckState({
      isChecking: false,
      userExists: true,
      userData: {
        firstName: user.firstName,
        lastName: user.lastName,
        name: `${user.firstName} ${user.lastName}`.trim(),
      },
    });
  };

  const roleOptions = roles.map(role => ({
    value: role.role,
    label: role.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: role.description,
  }));

  const permissionOptions = permissions.map(permission => ({
    value: permission,
    label: permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    description: `Permission to ${permission.replace(/_/g, ' ')}`,
  }));

  const selectedRole = roles.find(r => r.role === formData.role);

  if (!isOpen) return null;

  const drawerContent = (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-[110]" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-6 right-6 bottom-6 w-full max-w-2xl bg-white shadow-xl rounded-xl border border-gray-200 z-[120] flex flex-col">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="bg-red-100 p-2 rounded-lg">
              <UserPlus className="h-6 w-6 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {mode === 'edit' ? 'Edit Team Member' : 'Add Team Member'}
              </h3>
              <p className="text-sm text-gray-600">
                {mode === 'edit'
                  ? 'Update role, permissions, and status'
                  : 'Invite a new member to join your project team'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <form id="team-member-form" onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              {/* Email Section */}
              {mode === 'add' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={e => {
                          setFormData(prev => ({ ...prev, email: e.target.value }));
                          setSearchQuery(e.target.value);
                          setShowUserSearch(true);
                        }}
                        error={errors.email}
                      />

                      {/* Email Check Status */}
                      {formData.email && !errors.email && (
                        <div className="mt-2">
                          {emailCheckState.isChecking ? (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                              <span>Checking email...</span>
                            </div>
                          ) : emailCheckState.userExists ? (
                            <div className="flex items-center space-x-2 p-3 bg-brand-50 rounded-lg border border-brand-200">
                              <Users className="h-4 w-4 text-brand-primary" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-brand-900">
                                  Existing User: {emailCheckState.userData?.firstName}{' '}
                                  {emailCheckState.userData?.lastName}
                                </p>
                                <p className="text-xs text-brand-700">
                                  This user will receive an invitation email to join the project
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
                              <UserPlus className="h-4 w-4 text-green-500" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-green-900">New User</p>
                                <p className="text-xs text-green-700">
                                  We'll create an account for this user and send them an invitation
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* User Search Results */}
                      {showUserSearch && searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {searchResults.map(user => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleUserSelect(user)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-red-500">
                                    {user.firstName[0]}
                                    {user.lastName[0]}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </p>
                                <p className="text-sm text-gray-500 truncate">{user.email}</p>
                                {user.title && (
                                  <p className="text-xs text-gray-400">{user.title}</p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Name fields for new users */}
                  {!emailCheckState.userExists && formData.email && !emailCheckState.isChecking && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          First Name *
                        </label>
                        <Input
                          type="text"
                          placeholder="Enter first name"
                          value={formData.firstName}
                          onChange={e =>
                            setFormData(prev => ({ ...prev, firstName: e.target.value }))
                          }
                          error={errors.firstName}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Name *
                        </label>
                        <Input
                          type="text"
                          placeholder="Enter last name"
                          value={formData.lastName}
                          onChange={e =>
                            setFormData(prev => ({ ...prev, lastName: e.target.value }))
                          }
                          error={errors.lastName}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Role Selection */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                  <Dropdown
                    placeholder="Select role"
                    value={formData.role}
                    onChange={value => setFormData(prev => ({ ...prev, role: value as TeamRole }))}
                    options={roleOptions}
                    error={errors.role}
                  />
                  {selectedRole && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{selectedRole.description}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedRole.canManageTeam && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-100 text-brand-800">
                            Can Manage Team
                          </span>
                        )}
                        {selectedRole.canEditProject && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            Can Edit Project
                          </span>
                        )}
                        {selectedRole.canViewFinancials && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            Can View Financials
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <MultiDropdown
                    placeholder="Select permissions"
                    options={permissionOptions}
                    value={formData.permissions}
                    onChange={value => setFormData(prev => ({ ...prev, permissions: value }))}
                    error={errors.permissions}
                    maxHeight="200px"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Default permissions are automatically selected based on the role
                  </p>
                </div>
              </div>

              {/* Status (Edit mode only) */}
              {mode === 'edit' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <Dropdown
                      placeholder="Select status"
                      value={formData.status}
                      onChange={value => setFormData(prev => ({ ...prev, status: value as any }))}
                      options={[
                        { value: 'active', label: 'Active', description: 'Member has full access' },
                        {
                          value: 'inactive',
                          label: 'Inactive',
                          description: 'Member access is suspended',
                        },
                      ]}
                    />
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {mode === 'edit'
                      ? 'Update Message (Optional)'
                      : 'Invitation Message (Optional)'}
                  </label>
                  <TextArea
                    placeholder={
                      mode === 'edit'
                        ? 'Add a note about this update...'
                        : 'Welcome to our project! We look forward to working with you...'
                    }
                    value={formData.message}
                    onChange={value => setFormData(prev => ({ ...prev, message: value }))}
                    rows={3}
                  />
                </div>
              </div>

              {/* Send Invitation (Add mode only) */}
              {mode === 'add' && (
                <div className="flex items-center">
                  <input
                    id="send-invitation"
                    type="checkbox"
                    checked={formData.sendInvitation}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, sendInvitation: e.target.checked }))
                    }
                    className="h-4 w-4 text-red-500 focus:ring-red-400 border-gray-300 rounded"
                  />
                  <label htmlFor="send-invitation" className="ml-2 block text-sm text-gray-700">
                    Send invitation email to the new team member
                  </label>
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Sticky Footer */}
        <div className="border-t border-gray-200 p-6 bg-white rounded-b-xl">
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="team-member-form"
              disabled={isLoading}
              className="px-6 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-sm"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {mode === 'edit' ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {mode === 'edit' ? 'Update Member' : 'Add Member'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(drawerContent, document.body);
};

export default AddTeamMemberDrawer;
